import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { CardapioListResult, CardapioPayload, CardapioState } from '../types/cardapioTypes';

const initialState: CardapioState = {
  items: [],
  totalItemsCount: 0,
  loading: 'idle',
};

interface FetchCardapioParams {
  keyword?: string;
  dateFrom?: string;
  dateTo?: string;
  mealType?: number;
  pageNumber?: number;
  pageSize?: number;
}

function toFormData(payload: CardapioPayload) {
  const formData = new FormData();

  if (payload.id) formData.append('Id', String(payload.id));
  formData.append('Date', payload.date);
  formData.append('MealType', String(payload.mealType));
  formData.append('Dish', payload.dish);
  formData.append('HasDessert', String(payload.hasDessert));
  if (payload.dessertDescription) {
    formData.append('DessertDescription', payload.dessertDescription);
  }
  formData.append('IsSpecialDish', String(payload.isSpecialDish));

  return formData;
}

export const fetchCardapioList = createAsyncThunk<
  CardapioListResult,
  FetchCardapioParams | undefined
>('cardapio/fetchCardapioList', async (params, thunkApi) => {
  try {
    const { status, data } = await axios.get('/api/cardapio/getCardapio', { params });

    if (status === 204) {
      return { results: [], totalItemsCount: 0 };
    }

    return data;
  } catch (error) {
    return thunkApi.rejectWithValue(false);
  }
});

export const createCardapioItem = createAsyncThunk<boolean, CardapioPayload>(
  'cardapio/createCardapioItem',
  async (payload, thunkApi) => {
    try {
      const { status } = await axios.post('/api/cardapio/createCardapio', toFormData(payload));

      if (status >= 200 && status < 300) {
        return true;
      }
      return thunkApi.rejectWithValue(false);
    } catch (error) {
      return thunkApi.rejectWithValue(false);
    }
  }
);

export const updateCardapioItem = createAsyncThunk<boolean, CardapioPayload>(
  'cardapio/updateCardapioItem',
  async (payload, thunkApi) => {
    try {
      const { status } = await axios.put('/api/cardapio/updateCardapio', toFormData(payload));

      if (status >= 200 && status < 300) {
        return true;
      }
      return thunkApi.rejectWithValue(false);
    } catch (error) {
      return thunkApi.rejectWithValue(false);
    }
  }
);

export const deleteCardapioItem = createAsyncThunk<number, number>(
  'cardapio/deleteCardapioItem',
  async (cardapioId, thunkApi) => {
    try {
      const { status } = await axios.delete('/api/cardapio/deleteCardapio', {
        params: { cardapioId },
      });

      if (status >= 200 && status < 300) {
        return cardapioId;
      }
      return thunkApi.rejectWithValue(false);
    } catch (error) {
      return thunkApi.rejectWithValue(false);
    }
  }
);

const cardapioSlice = createSlice({
  name: 'cardapio',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder.addCase(fetchCardapioList.pending, state => {
      state.loading = 'pending';
    });
    builder.addCase(fetchCardapioList.fulfilled, (state, { payload }) => {
      state.items = payload.results ?? [];
      state.totalItemsCount = payload.totalItemsCount ?? 0;
      state.loading = 'succeeded';
    });
    builder.addCase(fetchCardapioList.rejected, state => {
      state.loading = 'failed';
    });

    builder.addCase(deleteCardapioItem.fulfilled, (state, { payload }) => {
      state.items = state.items.filter(item => item.id !== payload);
      state.totalItemsCount = Math.max(0, state.totalItemsCount - 1);
    });
  },
});

export default cardapioSlice.reducer;
